import { motion } from "framer-motion";
import { GraduationCap, MapPin, CheckCircle2 } from "lucide-react";
import { education, accentTokens } from "../data/mock";

export const Education = () => {
  return (
    <section
      id="education"
      className="relative py-24 md:py-32"
      data-testid="education-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
            // 06 — Education
          </p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-bold tracking-tighter text-white">
            Academic{" "}
            <span className="text-gradient-aurora animate-gradient-pan font-serif italic">
              background.
            </span>
          </h2>
        </motion.div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          {education.map((edu, idx) => {
            const t = accentTokens[edu.accent] || accentTokens.cyan;
            return (
              <motion.div
                key={edu.school}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.7,
                  delay: idx * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`relative gradient-border rounded-2xl glass p-6 sm:p-8 hover:-translate-y-1 transition-transform duration-300 ${t.glow}`}
                data-testid={`education-card-${idx}`}
              >
                <div className={`absolute -top-16 -right-16 h-44 w-44 ${t.blob} blur-3xl rounded-full pointer-events-none`} />

                <div className="relative flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl glass-strong ${t.text}`}>
                      <GraduationCap className="h-5 w-5" />
                    </div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-cyan-200/70">
                      {idx === 0 ? "Graduate School" : "Undergraduate"}
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full glass-strong text-[10px] font-mono uppercase tracking-widest ${t.text}`}>
                    <CheckCircle2 className="h-3 w-3" />
                    {edu.status}
                  </span>
                </div>

                <h3 className="relative font-display mt-6 text-xl sm:text-2xl font-semibold tracking-tight text-white leading-tight">
                  {edu.school}
                </h3>
                <p className="relative mt-2 font-serif italic text-lg text-cyan-100/90">
                  {edu.degree}
                </p>
                {edu.specialization && (
                  <p className={`relative mt-1 text-sm font-medium ${t.text}`}>
                    {edu.specialization}
                  </p>
                )}

                <div className="relative mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-cyan-200/70">
                  <span className="font-mono">{edu.period}</span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3 w-3" />
                    {edu.location}
                  </span>
                </div>

                {edu.gpa && (
                  <div className="relative mt-6 flex items-end justify-between gap-4 pt-5 border-t border-white/5">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-200/70">
                        GPA
                      </p>
                      <p className="font-display text-5xl font-bold mt-1 leading-none text-gradient-aurora">
                        {edu.gpa.split(" / ")[0]}
                      </p>
                      <p className="font-mono text-[10px] text-cyan-200/60 mt-1">
                        of {edu.gpa.split(" / ")[1]}
                      </p>
                    </div>
                  </div>
                )}

                <div className="relative mt-6 flex flex-wrap gap-2">
                  {edu.focus.map((f) => (
                    <span
                      key={f}
                      className={`font-mono text-[11px] px-2.5 py-1 rounded-full border ${t.chip}`}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
